/**
 * WeevilTrak Prediction Map Component
 * Main component that integrates all map sub-components
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { APIProvider, Map as GoogleMap } from "@vis.gl/react-google-maps";
import { MapContent } from "./components/MapContent";
import { CenterMarker } from "./components/CenterMarker";
import { HoverTooltip } from "./components/HoverTooltip";
import { convertPradictionToGeoJson, handleLocationSearchRequest } from "../../weevilTrakCommonFunc";
import { GeoJsonData, HoverInfo, IPredictionDataModel, IPredictionsModel } from "@/util/http/WeevilTrak/responseType";
import { RootState } from "@/redux/reducer/store";
import { useDispatch, useSelector } from "react-redux";
import { Spin } from "antd";
import downloadIcon from "@/resource/images/png/lightning/download-icon.png";
import { WeevilTrakTabsEnum } from "@/util/cosntants";
import ZoomControls from "./components/ZoomControls";
import { CircleComponent } from "./components/CircleComponent";
import "./index.less";
import html2canvas from "html2canvas";
import { updateIsLoadingForGetSearchResult } from "@/redux/actions/weevilTrakAction";
import { set } from "immer/dist/internal";
const APIKEY = "AIzaSyDlsxkoR8-fiIgncnDunmEtWEwDmr896H0";



// Map bounds to prevent panning into areas without map content
// Latitude limits: -85 to 85 (beyond this Google Maps has no content)
// Longitude limits: -180 to 180 (full world width)
const MAP_BOUNDS = {
    north: 85, // Maximum latitude (northern limit)
    south: -85, // Minimum latitude (southern limit)
    west: -180, // Minimum longitude (western limit)
    east: 180 // Maximum longitude (eastern limit)
};

const WeevilTrakMap: React.FC = () => {

    type ZoomChangedEvent = { detail: { zoom: number } };
    type CenterChangedEvent = { detail: { center: google.maps.LatLngLiteral } };

    const dispatch = useDispatch();

    const {
        searchStagesData,
        isLoadingForGetSearchResult,
        currentTab,
        // for location
        locationNameOrCoordinate,
        radiusValue,
        selectedLocationItem,
        needClearLocation,
        needClearRegion,
        currentStage
    } = useSelector((state: RootState) => state.weevTrakReducer);

    const mapContainerRef = useRef<HTMLDivElement>(null);

    // Track the actual rendered pixel height of the map container.
    // This updates whenever `.weevilTrak-google-map-content` changes size.
    const [mapContentHeight, setMapContentHeight] = useState<number | undefined>(undefined);
    useEffect(() => {
        const element = mapContainerRef.current;
        if (!element) return;

        const updateHeight = () => {
            const rect = element.getBoundingClientRect();
            let nextHeight = Math.round(rect.height);
            if(currentTab === WeevilTrakTabsEnum.Region){
                nextHeight = nextHeight - 65;
            }
            setMapContentHeight(nextHeight);
        };

        updateHeight();

        // Prefer ResizeObserver for dynamic layouts.
        const resizeObserver = new ResizeObserver(() => {
            updateHeight();
        });
        resizeObserver.observe(element);

        return () => {
            resizeObserver.disconnect();
        };
    }, [currentTab]);

    console.log("WeevilTrakMap render, searchStagesData: ", searchStagesData);
    // 地图的中心点状态
    const [center, setCenter] = useState<google.maps.LatLngLiteral>({lat: 38.0, lng: -95.0});
    const [zoom,setZoom] = useState<number>(5);
    const minZoom = 2; // 和 Map 组件的 minZoom 保持一致
    const maxZoom = 20; // 和 Map 组件的 maxZoom 保持一致

    // When true, MapContent can call map.fitBounds once to auto-fit.
    // Any explicit view control (reset/zoom buttons/manual pan) disables it.
    const [autoFitBoundsEnabled, setAutoFitBoundsEnabled] = useState<boolean>(true);
    const isAutoFittingRef = useRef(false);
    const isProgrammaticViewChangeRef = useRef(false);

    //=================== for CircleOverlay ==================
    const [locationNameForCenter, setLocationNameForCenter] = useState<string>("");
    useEffect(()=>{
        if(currentTab !== WeevilTrakTabsEnum.LOCATION) return;
        if(radiusValue === undefined || radiusValue < 0) return;

        if(locationNameOrCoordinate){
            const [longitude,latitude,locationName]= handleLocationSearchRequest(locationNameOrCoordinate, selectedLocationItem);


            if(longitude !== 0 && latitude !== 0){
                if(longitude !== center.lng || latitude !== center.lat){
                    setCenter({ lat: latitude, lng: longitude });
                }
            }
            setLocationNameForCenter(locationName ?? "");

        }
    },[radiusValue, selectedLocationItem, locationNameOrCoordinate]);

    //=================== for CircleOverlay ==================

    useEffect(()=>{
        if(needClearLocation || needClearRegion){
            setAutoFitBoundsEnabled(false);
            isProgrammaticViewChangeRef.current = true;
            setCenter({lat: 38.0, lng: -95.0});
            setZoom(5);
            setLocationNameForCenter("");
        }
    },[needClearLocation,needClearRegion]);

    // ================== for zoom control ==================
    // 处理放大地图
    const handleZoomIn = useCallback(() => {
        setAutoFitBoundsEnabled(false);
        isProgrammaticViewChangeRef.current = true;
        setZoom(prevZoom => Math.min(prevZoom + 1, maxZoom));
    }, [maxZoom]);

    // 处理缩小地图
    const handleZoomOut = useCallback(() => {
        setAutoFitBoundsEnabled(false);
        isProgrammaticViewChangeRef.current = true;
        setZoom(prevZoom => Math.max(prevZoom - 1, minZoom));
    }, [minZoom]);

    // 监听 Map 组件自身的缩放变化，同步到 state
    const handleMapZoomChanged = useCallback((event: ZoomChangedEvent) => {
        // If this zoom change is caused by our own setCenter/setZoom, don't treat it as a user action.
        if (!isAutoFittingRef.current && !isProgrammaticViewChangeRef.current) {
            setAutoFitBoundsEnabled(false);
        }
        setZoom(event.detail.zoom);
        if (isProgrammaticViewChangeRef.current) {
            isProgrammaticViewChangeRef.current = false;
        }
    }, []);

    // 监听 Map 组件自身的中心点变化，同步到 state
    const handleMapCenterChanged = useCallback((event: CenterChangedEvent) => {
        if (!isAutoFittingRef.current && !isProgrammaticViewChangeRef.current) {
            setAutoFitBoundsEnabled(false);
        }
        setCenter(event.detail.center);
        if (isProgrammaticViewChangeRef.current) {
            isProgrammaticViewChangeRef.current = false;
        }
    }, []);
    const handleMapCenterForCurrent = useCallback((lat:number,lng:number) => {
        setAutoFitBoundsEnabled(false);
        isProgrammaticViewChangeRef.current = true;
        setCenter({lat:lat,lng:lng});
    }, []);
    // ================== for zoom control ==================




    const [geoJsonData, setGeoJsonData] = useState<GeoJsonData>({ type: "FeatureCollection", features: [] });
    const [predictionData, setPredictionData] = useState<IPredictionDataModel[]>([]);
    const [hoverInfo, setHoverInfo] = useState<HoverInfo | null>(null);
    const [centerPredictionData, setCenterPredictionData] = useState<IPredictionsModel | null>(null);

    useEffect(() => {
        if(!searchStagesData) {
            setGeoJsonData({ type: "FeatureCollection", features: [] });
            setPredictionData([]);
            setAutoFitBoundsEnabled(false);
            return;
        }
        const pradictionJson = searchStagesData;
        const geoJson = convertPradictionToGeoJson(pradictionJson);
        setGeoJsonData(geoJson);
        console.log(geoJson);

        const data: IPredictionDataModel[] = pradictionJson?.locations?.map(p => ({
            location: `${p.latitude}_${p.longitude}`,
            predictedDays: p?.predictions?.[0]?.predictedDays ?? p?.predictions?.[0]?.predictedDays ?? 0
        })) ?? [];

        setPredictionData(data);

        // Only enable auto-fit for Region tab. Location tab is controlled by user input.
        setAutoFitBoundsEnabled(currentTab === WeevilTrakTabsEnum.Region || currentTab === WeevilTrakTabsEnum.LOCATION);
    }, [searchStagesData]);

    useEffect(()=>{

        if(searchStagesData?.center){
            if(currentTab === WeevilTrakTabsEnum.Region){
                setCenterPredictionData(null);
            }
            else{
                const currentStageIndex = Number(currentStage?.replace("Stage", ""));
                const tmpData = searchStagesData?.center?.predictions?.find(p => p.stageId === currentStageIndex);
                setCenterPredictionData(tmpData ?? null);
            }

        }
        else{
            setCenterPredictionData(null);
        }

    },[searchStagesData?.center,currentTab]);

    console.log("WeevilTrakMap render - searchStagesData: ", searchStagesData);
    console.log("WeevilTrakMap render - predictionData: ", predictionData);
    console.log("WeevilTrakMap render - geoJsonData: ", geoJsonData);
    console.log("WeevilTrakMap render - locationNameForCenter: ", locationNameForCenter);

    console.log("new test searchStagesData: ", searchStagesData);
    console.log("new test currentStage: ", currentStage);
    console.log("new test centerPredictionData: ", centerPredictionData);


    const handleHover = useCallback((info: HoverInfo | null) => {
        setHoverInfo(info);
    }, []);

    const [totalHeight, setTotalHeight] = useState("calc(100vh - 400px)");
    useEffect(()=>{
        let mapHeight;
        if (currentTab === WeevilTrakTabsEnum.Region) {
            mapHeight = 65 ;
            setTotalHeight(`calc(100vh - ${mapHeight}px)`);
            return;
        }
        if(currentTab === WeevilTrakTabsEnum.LOCATION){
            // mapHeight = 60 + (259 + 20*2) + (34 + 10*2) + 20*2 + 40 + 47;
            mapHeight = 60 + (200 + 20*2) ;
            setTotalHeight(`calc(100vh - ${mapHeight}px)`);
        }

    },[currentTab]);

    const [centerMarkerCenter, setCenterMarkerCenter] = useState<google.maps.LatLngLiteral>({
        lat: 0,
        lng: 0
    });
    useEffect(()=>{
        if(!searchStagesData){
            setCenterMarkerCenter({ lat: 0, lng: 0 });
            return;
        }
        else{
            const center = {
                lat: searchStagesData?.center?.latitude,
                lng: searchStagesData?.center?.longitude
            };
            setCenterMarkerCenter(center);
        }
    },[searchStagesData]);

    const downloadImage = ()=>{
        dispatch(updateIsLoadingForGetSearchResult(true));
        html2canvas(mapContainerRef.current, {
            useCORS: true,
            allowTaint: true,
            ignoreElements: (element) => {
                return element.classList.contains("no-screenshot");
            },
            backgroundColor: null, // 允许透明背景
        }).then((canvas) => {
            const dataUrl = canvas.toDataURL("image/png");
            console.log("test images",dataUrl);

            const link = document.createElement("a");
            link.href = dataUrl;
            link.download = currentStage + ".png";
            link.click();
            // element1.style.display = "block";
            dispatch(updateIsLoadingForGetSearchResult(false));
        });

    };

    console.log("dddddd center: ",center);
    console.log("dddddd zoom: ",zoom);
    console.log("totalHeight",totalHeight);
    console.log("currentStage",currentStage);

    return (
        // <div style={{ width: "1000px", height: "600px", position: "relative" }}>
        <div className="weevilTrak-google-map" style={{ width: "100%", minHeight: "500px" }}>
            <Spin spinning={isLoadingForGetSearchResult} >
                {/* Download Image */}
                <div className="weevilTrak-map-download-image">

                    <div className="weevilTrak-map-download-image-icon"

                        onClick={()=>{
                            if(searchStagesData && searchStagesData.locations && searchStagesData.locations.length >=1){
                                downloadImage();
                            }
                        }}
                    >
                        <img
                            style={{ cursor: (searchStagesData?.locations?.length ?? 0) >= 1 ? "pointer" : "not-allowed" }}
                            src={downloadIcon} />
                    </div>
                    <div className="weevilTrak-map-download-image-title">Download Image</div>
                </div>
                <div className="weevilTrak-google-map-content"
                    style={{
                        // backgroundColor:"aqua",
                        height: totalHeight,
                        minHeight: "500px",
                        position: "relative",
                        display: "block"
                    }}
                    ref={mapContainerRef}
                >

                    {/* Google Map */}
                    <APIProvider apiKey={APIKEY} libraries={["drawing", "geometry"]}>
                        <GoogleMap
                            center={center} // 绑定中心点状态
                            zoom={zoom} // 绑定缩放级别状态
                            onZoomChanged={handleMapZoomChanged} // 监听地图自身的缩放变化
                            onCenterChanged={handleMapCenterChanged} // 监听地图自身的中心点变化
                            gestureHandling={"greedy"}
                            disableDefaultUI={true}
                            minZoom={minZoom} // 使用定义的 minZoom
                            maxZoom={maxZoom} // 使用定义的 maxZoom
                            mapTypeId={"roadmap"}
                            style={{
                                // backgroundColor: "red"
                            }}
                            restriction={{
                                latLngBounds: MAP_BOUNDS,
                                strictBounds: true
                            }}
                        >
                            <MapContent
                                geoJsonData={geoJsonData}
                                predictionData={predictionData}
                                onHover={handleHover}
                                autoFitBounds={autoFitBoundsEnabled}
                                onAutoFitStart={() => {
                                    isAutoFittingRef.current = true;
                                }}
                                onAutoFitEnd={() => {
                                    isAutoFittingRef.current = false;
                                    // Disable after one run to avoid overriding explicit center/zoom later.
                                    setAutoFitBoundsEnabled(false);
                                }}
                                center={centerMarkerCenter}
                                radius={radiusValue}
                                currentTab={currentTab}
                            />

                            {
                                currentTab === WeevilTrakTabsEnum.LOCATION && radiusValue !== undefined && radiusValue >= 0 &&
                                centerMarkerCenter?.lat !==0 && centerMarkerCenter?.lng !==0 && (
                                    <CenterMarker
                                        position={centerMarkerCenter}
                                        onHover={handleHover}
                                        locationName={locationNameForCenter}
                                        stage={centerPredictionData?.stage}
                                        predictionDate={centerPredictionData?.predictionDate}
                                        predictionStageDate={centerPredictionData?.predictedStageDate}
                                        predictedDays={centerPredictionData?.predictedDays}

                                        // stage={searchStagesData?.center?.predictions?.[0]?.stage}
                                        // predictionDate={searchStagesData?.center?.predictions?.[0]?.predictionDate}
                                        // predictionStageDate={searchStagesData?.center?.predictions?.[0]?.predictedStageDate}
                                        // predictedDays={searchStagesData?.center?.predictions?.[0]?.predictedDays}
                                    />
                                )
                            }
                            {/* Show Circle component */}
                            {
                                currentTab === WeevilTrakTabsEnum.LOCATION && radiusValue !== undefined && radiusValue >= 0 &&
                                centerMarkerCenter?.lat !==0 && centerMarkerCenter?.lng !==0 && (
                                    <CircleComponent
                                        center={centerMarkerCenter}
                                        radius={radiusValue}
                                        strokeColor="green"
                                        strokeOpacity={0.8}
                                        strokeWeight={2}
                                    />
                                )
                            }

                        </GoogleMap>
                        {/* Always show Zoom Control */}
                        <ZoomControls
                            onZoomIn={handleZoomIn}
                            onZoomOut={handleZoomOut}
                            handleMapCenterForCurrent = {handleMapCenterForCurrent}
                            currentZoom={zoom} // 传递当前缩放级别以控制按钮禁用状态
                            minZoom={minZoom}
                            maxZoom={maxZoom}
                            mapContentHeight={mapContentHeight}
                        />
                    </APIProvider>
                </div>

            </Spin>


            {/* Hover Info Tooltip */}
            <HoverTooltip hoverInfo={hoverInfo} />
        </div>
    );
};

export default WeevilTrakMap;
